/**
 * 마이클래스 상태: 참여가능 | 참여중 | 이수완료 | 종료
 * - 참여가능(eligible): 수강신청 완료, 프로덕트 사용자 등록
 * - 참여중(participating): 세션 시작 클릭, 초대 수락
 * - 이수완료(completed): 이수조건 도달 → 이수증 다운로드 가능
 * - 종료(ended): 수강기간 종료 (저장값 아님, participationPeriod.endDate로 계산)
 */

const MY_CLASS_STATUS_KEY = 'myClassStatus';
const PARTICIPATING_CLASSES_KEY = 'participatingClasses';

export type MyClassStoredStatus = 'eligible' | 'participating' | 'completed';

export type MyClassDisplayStatus = MyClassStoredStatus | 'ended';

export function getMyClassStatusMap(): Record<string, MyClassStoredStatus> {
  try {
    const raw = localStorage.getItem(MY_CLASS_STATUS_KEY) || '{}';
    return JSON.parse(raw) as Record<string, MyClassStoredStatus>;
  } catch {
    return {};
  }
}

/**
 * 특정 클래스를 참가 등록에서 제거 (participatingClasses, myClassStatus 둘 다에서 삭제).
 */
export function removeClassEnrollment(classId: string): void {
  const map = getMyClassStatusMap();
  if (map[classId]) {
    delete map[classId];
    localStorage.setItem(MY_CLASS_STATUS_KEY, JSON.stringify(map));
  }
  const participating = JSON.parse(localStorage.getItem(PARTICIPATING_CLASSES_KEY) || '{}') as Record<string, boolean>;
  if (participating[classId]) {
    delete participating[classId];
    localStorage.setItem(PARTICIPATING_CLASSES_KEY, JSON.stringify(participating));
  }
}

/**
 * 클래스 상태 저장. participatingClasses에도 반영해 목록 노출/링크 분기에 사용.
 */
export function setMyClassStatus(classId: string, status: MyClassStoredStatus): void {
  const map = getMyClassStatusMap();
  map[classId] = status;
  localStorage.setItem(MY_CLASS_STATUS_KEY, JSON.stringify(map));

  const participating = JSON.parse(localStorage.getItem(PARTICIPATING_CLASSES_KEY) || '{}') as Record<string, boolean>;
  participating[classId] = true;
  localStorage.setItem(PARTICIPATING_CLASSES_KEY, JSON.stringify(participating));
}

/** YYYY-MM-DD(또는 ISO 접두) — 당일 23:59:59.999(로컬)까지는 미종료 */
export function isParticipationEndYmdPassedLocal(endYmdOrIso: string | undefined): boolean {
  if (!endYmdOrIso || !String(endYmdOrIso).trim()) return false;
  const ymd = String(endYmdOrIso).trim().slice(0, 10);
  const parts = ymd.split('-').map((x) => parseInt(x, 10));
  if (parts.length !== 3 || parts.some((n) => !Number.isFinite(n))) return false;
  const [y, m, d] = parts;
  const endLocal = new Date(y, m - 1, d, 23, 59, 59, 999);
  return Date.now() > endLocal.getTime();
}

/**
 * 카드에 표시하는 수강기간 + isActive — 비활성이거나 종료일 지나면 종료.
 * (마이클래스·기관 목록 배지와 동일 규칙)
 */
export function isMyClassItemEndedBySchedule(item: {
  isActive?: boolean;
  participationPeriod?: { startDate?: string; endDate?: string };
}): boolean {
  if (item.isActive === false) return true;
  return isParticipationEndYmdPassedLocal(item.participationPeriod?.endDate);
}

/**
 * 표시용 상태: 수강기간 종료·비활성이면 'ended', 아니면 저장된 상태 반환.
 */
export function getDisplayStatus(
  storedStatus: MyClassStoredStatus | undefined,
  endDateStr: string | undefined,
  isActive?: boolean
): MyClassDisplayStatus {
  if (isActive === false) return 'ended';
  if (isParticipationEndYmdPassedLocal(endDateStr)) return 'ended';
  if (!storedStatus) return 'eligible';
  return storedStatus;
}

export const DISPLAY_STATUS_LABELS: Record<MyClassDisplayStatus, string> = {
  eligible: '참여가능',
  participating: '참여중',
  completed: '이수완료',
  ended: '종료',
};
